/**
 * Default set of header values which should be sent with every request unless explicitly provided through options.
 *
 * @type {Record<string, string>}
 */
const DEFAULT_HEADERS = {
    // The backend uses the accept header as a condition for considering an inocming request as a REST request.
    // See: https://core.trac.wordpress.org/ticket/44534
    Accept: 'application/json, */*;q=0.1',
};

/**
 * Default set of fetch option values which should be sent with every request unless explicitly provided through options.
 *
 * @type {Object}
 */
const DEFAULT_OPTIONS = {
    credentials: 'include',
};

/**
 * Fetch handler using async/await syntax. Checks for network errors and correct response format. Accepts and passes all options supported by `window.fetch`. Additionally pass in the URL to interact with and if you wish a data prop which is just a shorthand to set the body as JSON (and adjust the header as needed).
 *
 * Make sure to return JSON from your endpoint, all other response types will throw a type error.
 *
 * @author rafhun <rafhun@cubetech.ch>
 *
 * @export
 *
 * @param {Object} options request options
 * @param {string} options.url The complete URL to interact with
 * @param {Object} options.data Shorthand for the body prop, will be stringified and sent as JSON
 *
 * @returns {Promise} Resolves to a JS object
 */
export async function fetchHandler(options) {
    const { url, data, ...remainingOptions } = options;
    let { body, headers } = options;

    // merge explicitly-provided headers with defaults
    headers = { ...DEFAULT_HEADERS, ...headers };

    // The `data` property is a shorthand for sending a JSON body.
    if (data) {
        body = JSON.stringify(data);
        headers['Content-Type'] = 'application/json';
    }

    try {
        const response = await fetch(
            // fall back to `window.location` which is the behavior if `undefined` is passed
            url || window.location.href,
            {
                ...DEFAULT_OPTIONS,
                ...remainingOptions,
                body,
                headers,
            }
        );

        // This returns false on network errors (non 2xx status in response), @see https://developer.mozilla.org/en-US/docs/Web/API/Response/ok
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        // Check that the API actually has returned JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            throw new TypeError("Oops, we haven't got JSON!");
        }

        const posts = await response.json();

        return posts;
    } catch (error) {
        console.error('Error during fetch', error);
    }
}
